import { createFileRoute } from '@tanstack/react-router';
import { ensureLoaded } from '@/lib/ensure-loaded';
import { reviewQueueQuery } from '@/services/review';
import { homeSummaryQuery } from '@/services/home';
import { homePinsQuery } from '@/services/home-pins';
import { isApiKeySession } from '@/services/team';
import { brandDealsQuery } from '@/services/brand-deals';
import { feedbackListQuery } from '@/services/feedback';
import { myRoleQuery } from '@/services/team-members';
import { welcomeQuery } from '@/services/welcome';
import { HomePage } from '@/features/home/home-page';
import { startsOnOperatorHome } from '@/features/operator/operator-start';
import { StartPage } from '@/features/operator/start-page';
import { loadOperatorHome } from '@/features/operator/operator-home-load';

export const Route = createFileRoute('/_authed/')({
  // Prefetch everything Home shows so it renders complete on first paint.
  loader: async ({ context: { queryClient, team, user, mock } }) => {
    if (!team) return undefined;
    // Whether the welcome plays here (a first sign-in) is known before the first paint, so Home never draws its boxes
    // and then swaps them; a read that fails leaves Home as it is. Never asked for in mock data or with an API key.
    const asksWelcome = !mock && !isApiKeySession(team);
    const readWelcome = () => queryClient.ensureQueryData(welcomeQuery(team.id)).catch(() => null);
    // An operator's Home: its code, his pins and what the starting boxes draw, together, so it is whole on first paint
    // (a box whose read fails says so itself); a client seat never waits here.
    if (await startsOnOperatorHome(queryClient, { team, user })) {
      const settle = (read: Promise<unknown>) => read.catch(() => null);
      return Promise.all([
        loadOperatorHome(),
        // A dev key (a team, no person) has no pins of its own: its Home shows the starting boxes.
        isApiKeySession(team)
          ? null
          : settle(
              queryClient
                .ensureQueryData(homePinsQuery(team.id))
                .then((pins) => import('@/features/home/pinned/inbox-read').then(({ preloadPinnedConversations }) => preloadPinnedConversations(queryClient, team.id, pins))),
            ),
        settle(ensureLoaded(queryClient, reviewQueueQuery(team.id))),
        // The Inbox's code loads with its read, as Draper's corner loads it (the shell never waits on it).
        settle(import('@/features/home/pinned/inbox-read').then(({ inboxWaitingRead }) => queryClient.ensureQueryData(inboxWaitingRead(team.id)))),
        settle(queryClient.ensureQueryData(brandDealsQuery(team.id))),
        asksWelcome ? readWelcome() : null,
      ]);
    }
    // Finished requests are a row on Home, not worth holding the page for.
    void queryClient.prefetchQuery(feedbackListQuery(team.id));
    // Her role decides what Home lists and whether it shows money; an API without roles answers 404 (nothing narrows).
    // `ensureLoaded`: the shell watches the queue too, and a read it lets go of mid-way is cancelled, not failed (O5).
    return Promise.all([
      ensureLoaded(queryClient, reviewQueueQuery(team.id)),
      ensureLoaded(queryClient, homeSummaryQuery(team.id)),
      // An Agency seat never gets the welcome: it is asked for once her role says she isn't one.
      ensureLoaded(queryClient, myRoleQuery(team.id))
        .catch(() => null)
        .then((role) => (asksWelcome && role?.roleId !== 'agency' ? readWelcome() : null)),
    ]);
  },
  component: () => <StartPage home={HomePage} />,
});
